import React, { PureComponent } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipFormatter,
  LabelFormatter,
} from 'recharts';
import colors from '@spotify-internal/encore-foundation';
import { formatDate, formatByOrderOfMagnitude, commaFormatNumber } from '../../../../lib/Formatter';
import chartStyles from 'styles/charts.module.scss';
import { Follower } from '../../../../types';
import { FormattedMessage, FormattedNumber, injectIntl, IntlShape } from 'react-intl';

type Props = {
  performanceData: Follower[];
  fill?: string;
  stroke?: string;
  name?: string;
  intl: IntlShape;
};

class InternationalizedPerformanceOverTimeChart extends PureComponent<Props> {
  static defaultProps = {
    fill: colors.storm,
    stroke: '#4a90e2',
    name: undefined,
  };

  toolTipFormatter: TooltipFormatter =
    /* istanbul ignore next */
    (value, key, { color }) => {
      return (
        <div>
          <span
            className={chartStyles['audience-tooltip-circle']}
            style={{ backgroundColor: color }}
          />
          <span className={chartStyles['audience-tooltip-label']}>
            <FormattedMessage
              id={`network.audience.listeners.tooltip_${key}`}
              description={`Tooltip key ${key}`}
              defaultMessage={key}
            />
          </span>
          <span className={chartStyles['audience-tooltip-value']}>
            <FormattedNumber value={value as number} />
          </span>
        </div>
      );
    };

  formatLabel: LabelFormatter =
    /* istanbul ignore next */
    label => {
      return this.props.intl
        .formatDate(new Date(label), {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
        .toUpperCase();
    };

  formatXAxisTick: LabelFormatter = label =>
    this.props.intl
      .formatDate(new Date(label), {
        day: 'numeric',
        month: 'short',
        year: undefined,
      })
      .toUpperCase();

  formatYAxisTick: LabelFormatter = label =>
    this.props.intl
      .formatNumber(label as number, {
        notation: 'compact',
        maximumSignificantDigits: 3,
      })
      .toLowerCase();

  render() {
    const { performanceData, fill, stroke, name } = this.props;
    return (
      <div data-qa="perfomance-over-time-chart" style={{ width: '100%', height: '350px' }}>
        <ResponsiveContainer height={350}>
          <AreaChart
            className={chartStyles.chart}
            data={performanceData}
            margin={{ top: 15, right: 10, left: -20, bottom: 0 }}
          >
            <XAxis
              axisLine={false}
              dataKey="date"
              tickFormatter={this.formatXAxisTick}
              tickLine={false}
            />
            <YAxis axisLine={false} tickFormatter={this.formatYAxisTick} tickLine={false} />
            <CartesianGrid vertical={false} />
            <Tooltip
              isAnimationActive={false}
              itemStyle={{ color: 'unset', padding: '4px 0' }}
              wrapperStyle={{
                borderRadius: '5px',
                boxShadow: '0 3px 8px 0 rgba(0, 0, 0, 0.2)',
                borderColor: 'white',
              }}
              formatter={this.toolTipFormatter}
              labelFormatter={this.formatLabel}
              separator=" "
            />
            <Area
              name={name}
              type="linear"
              dataKey="count"
              stackId="1"
              stroke={stroke}
              fill={fill}
              strokeWidth={2}
              activeDot={{ strokeWidth: 3, r: 7 }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }
}

const PerformanceOverTimeChart = injectIntl(InternationalizedPerformanceOverTimeChart);
export { PerformanceOverTimeChart };
